import {Button, Divider, Stack, Typography} from '@mui/material';
import React, {Fragment, useState} from 'react';
import {useFormContext} from 'react-hook-form';
import {useLocation, useNavigate} from 'react-router-dom';

import {APIError} from 'data/errors';
import {
	AlertModal,
	MaskedInput,
	PasswordWithStrengthValidation,
	Snackbar,
	TextInput,
} from 'presentation/components';
import {Loading} from 'presentation/components/Loading/Loading';
import {useHostName} from 'presentation/hooks';
import Icon from 'presentation/icons';
import {useFirstAccess} from 'presentation/pages/public/hooks';
import {StyledFixedFooter} from 'presentation/pages/public/styles';
import {translations} from 'presentation/translations-new';
import {getLoginIdentifier, masks} from 'presentation/utils';

import {setLoginToken} from 'infra/http/utils';
import {
	OperationStateType,
	OperationStateTypesEnum,
	SelectionFlowEnum,
} from 'presentation/constants';
import {TermsOfUseAndPrivacyPolicy} from 'presentation/pages/public/components/TermsOfUseAndPrivacyPolicy';
import {PublicFlowEnum} from 'presentation/pages/public/constants/PublicFlowEnum';
import {useRemoteAuthenticationMutation} from 'presentation/pages/public/mutation/useRemoteAuthenticationMutation';
import {IFormData} from './schema';

const {formLabels, formPlaceholders, actionText, firstAccess} = translations['pt-br'].publicFlow;
const {serverErrorSnackbar, close} = translations['pt-br'].commons;

export function FirstAccessForm() {
	const {
		handleSubmit,
		register,
		getFieldState,
		getValues,
		formState: {errors, isSubmitting, isValid},
	} = useFormContext<IFormData>();
	const {state} = useLocation();
	const navigate = useNavigate();
	const {
		showConfirmationModal,
		setAlertModalState,
		alertModalState,
		handleSubmitRequest,
		showAPIError,
	} = useFirstAccess();
	const {
		dataHostNameState: {product, productCode},
	} = useHostName();

	const {successModal, errorModal} = firstAccess;
	const successModalVariant = successModal[product as keyof typeof successModal];
	const {invalid: invalidCellphone, isDirty: dirtyCellphone} = getFieldState('cellphone');
	const {invalid: invalidEmail, isDirty: dirtyEmail} = getFieldState('email');
	const [requestState, setRequestState] = useState<OperationStateType | null>(null);
	const loginLoading = requestState === OperationStateTypesEnum.PENDING;
	const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
	const handleCloseSnackbar = () => {
		setIsSnackbarOpen(false);
	};

	const {mutateAsync: authenticate} = useRemoteAuthenticationMutation();

	const handleLogin = async () => {
		try {
			setRequestState(OperationStateTypesEnum.PENDING);
			const identifier = getLoginIdentifier(state.login);
			if (identifier === '0') {
				setRequestState(OperationStateTypesEnum.ERROR);
				setIsSnackbarOpen(true);
				return;
			}
			const {login_token} = await authenticate({
				cdEmpresaPlano: productCode,
				identificador: identifier,
				senha: getValues('password'),
				usuario: state.login,
			});
			setLoginToken(login_token);
			setRequestState(OperationStateTypesEnum.DONE);
			navigate(SelectionFlowEnum.PLAN_SELECTION);
		} catch (error) {
			if (error instanceof APIError) {
				setRequestState(OperationStateTypesEnum.ERROR);
				setIsSnackbarOpen(true);
			}
		}
	};

	return (
		<Fragment>
			<Stack component="form" gap={2} onSubmit={handleSubmit(handleSubmitRequest)}>
				<Stack>
					<Typography variant="label" component="label" htmlFor="email">
						{formLabels.email}
					</Typography>
					<TextInput
						name="email"
						inputMode="email"
						autoComplete="current-email"
						label={formPlaceholders.email}
						error={dirtyEmail && invalidEmail}
						helperText={dirtyEmail ? errors.email?.message : undefined}
					/>
				</Stack>
				<Stack>
					<Typography variant="label" component="label" htmlFor="cellphone">
						{formLabels.cellphone}
					</Typography>
					<MaskedInput
						{...register('cellphone')}
						id="cellphone"
						inputMode="tel"
						maskOptions={masks.cellphone}
						autoComplete="current-cellphone"
						error={dirtyCellphone && invalidCellphone}
						helperText={dirtyCellphone ? errors.cellphone?.message : undefined}
						label={formPlaceholders.cellphone}
						$fieldName="cellphone"
					/>
				</Stack>
				<Divider />
				<Typography>{firstAccess.createNewPassTitle}</Typography>
				<PasswordWithStrengthValidation flowType={PublicFlowEnum.SIGN_UP} />
				<TermsOfUseAndPrivacyPolicy />
				<StyledFixedFooter>
					<Button
						fullWidth
						size="large"
						disabled={!isValid || isSubmitting}
						type="submit"
						endIcon={isSubmitting ? <Loading color="inherit" size={16} /> : null}>
						{firstAccess.actionText.createNewPassword}
					</Button>
				</StyledFixedFooter>
			</Stack>
			{showConfirmationModal ? (
				<AlertModal
					variant="success"
					title={successModalVariant.title}
					headerTitle={successModalVariant.headerTitle}
					description={successModalVariant.description}
					open={alertModalState}
					onClose={() => setAlertModalState(false)}
					footerContent={
						<Button
							fullWidth
							size="large"
							disabled={loginLoading}
							endIcon={
								loginLoading ? (
									<Loading color="inherit" size={16} />
								) : (
									<Icon name="arrow-right-filled" />
								)
							}
							onClick={handleLogin}>
							{actionText.signIn}
						</Button>
					}
				/>
			) : null}

			{showAPIError ? (
				<AlertModal
					variant="error"
					title={errorModal.title}
					headerTitle={errorModal.headerTitle}
					description={errorModal.description}
					open={alertModalState}
					onClose={() => setAlertModalState(false)}
					footerContent={
						<Button fullWidth size="large" onClick={() => setAlertModalState(false)}>
							{close}
						</Button>
					}
				/>
			) : null}
			<Snackbar
				onClose={handleCloseSnackbar}
				message={serverErrorSnackbar}
				open={isSnackbarOpen}
			/>
		</Fragment>
	);
}
